import styled, { css } from 'styled-components';

interface ActiveDivProps {
  /*! set hideFocus to TRUE if element is already wrapped by button, anchor or other accessibility element and you want to hide ActiveDiv focus styling */
  $hideFocus?: boolean;
  /*! If you want disable item selection by tab, set tabIndex to -1 */
  tabIndex?: number;
  /*! Choose a proper role: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques */
  role?: string;
}

/*!
 * This component can handle accessibility issues for div elements.
 * Please remember you should always favour a proper HTML element (button, anchor etc..) over the ActiveDiv element
 */
export const ActiveDiv = styled.div.attrs<ActiveDivProps>(({ role, tabIndex, onClick, onKeyDown }) => ({
  role: role || 'button',
  tabIndex: tabIndex || 0,
  onKeyDown: onKeyDown
    ? (event: React.KeyboardEvent<HTMLDivElement>) => onKeyDown(event)
    : (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
          // onKeyDown and onClick has different events. This situation can potentially create a bug.
          // First of all this component shouldn't be use. Try to use Button or Anchor or provide valid onKeyDown action.
          const mouseEvent = {
            ...event,
            screenX: 0,
            screenY: 0,
            detail: 0,
            movementX: 0,
            movementY: 0,
            pageX: 0,
            pageY: 0,
            button: 0,
            buttons: 0,
            clientX: 0,
            clientY: 0
          } as unknown as React.MouseEvent<HTMLDivElement>;
          onClick?.(mouseEvent);
        }
      }
}))<ActiveDivProps>`
  cursor: pointer;
  ${({ $hideFocus = false }) => (!$hideFocus ? StyledFocusedStyled : StyledUnfocusedStyled)}
`;
const StyledFocusedStyled = css`
  :active,
  :focus {
    box-shadow:
      0px 3px 5px -1px rgba(0, 0, 0, 0.2),
      0px 6px 10px 0px rgba(0, 0, 0, 0.14),
      0px 1px 18px 0px rgba(0, 0, 0, 0.12);
  } // Material UI box-shadow
`;

const StyledUnfocusedStyled = css`
  cursor: inherit;
`;
